import type { HeadcountTrendDto } from '../../api/hr-employee';

/**
 * 인사 통계로 옮겨 단 현황 (2026-10-18 · web 레인 W10-26 · 사용자 결정 «빠지는 현황 숫자 다섯은 26 으로»)
 *
 * 25 직원 관리(W10-25) · 55 경력(W10-55)의 요약 카드를 걷으며 다른 화면에 없던 값이다. **서버 0** — 이미 부르는 응답 그대로.
 *   · 월별 재직 인원 · 전달 대비 증감 — `getHeadcountTrend(n)`(25 의 `EmployeeKpis` 가 부르던 것 · 추정치)
 *   · 입사 전 경력 등록 · 평균 — `listRecords()` 의 `summary`(55 가 부르는 것)
 * 시안 26 «둘째 손질»: 1월부터 · 막대 = 전달 대비 증감(0 기준선) · 재직 인원은 막대 아래 숫자 · «출처 …» 는 링크가 아니라 글.
 */

export interface MonthDelta {
  /** `YYYY-MM` */
  month: string;
  active: number;
  /** 전달 대비 — 전달 값을 모르면(12 월에 받는 창이 1 월 앞까지 안 닿을 때) null */
  delta: number | null;
}

/** 그 해 1월부터의 달별 증감. 서버 값은 «오늘부터 최근 n 달»이라 그 해 밖 달은 버리고, 맨 앞 달의 앞달은 같은 응답에서 찾는다. */
export function monthlyDeltas(points: HeadcountTrendDto['months'], year: number): MonthDelta[] {
  const sorted = [...points].sort((a, b) => a.month.localeCompare(b.month));
  const byMonth = new Map(sorted.map((p) => [p.month, p.active]));
  const prevOf = (ym: string) => {
    const [y, m] = ym.split('-').map(Number) as [number, number];
    const d = new Date(Date.UTC(y, m - 2, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  };
  return sorted
    .filter((p) => p.month.startsWith(`${year}-`))
    .map((p) => {
      const prev = byMonth.get(prevOf(p.month));
      return { month: p.month, active: p.active, delta: prev === undefined ? null : p.active - prev };
    });
}

/** 1 월부터 이번 달까지 + 전달 하나 — 서버 상한 12. */
export function trendMonthsFor(monthNow: number): number {
  return Math.min(12, monthNow + 1);
}

const signed = (n: number) => (n > 0 ? `+${n}` : String(n));

export function MonthlyHeadcountCard({ trend, year, thisYear }: {
  trend: HeadcountTrendDto | null | undefined;
  year: number;
  thisYear: number;
}) {
  const head = (
    <h2 className="hrpage__panelt">
      월별 재직 인원 · 전달 대비 증감
      <span className="hrpage__panels">출처 직원 관리{trend?.estimated ? ' · 추정치' : ''}</span>
    </h2>
  );
  if (year !== thisYear) {
    return (
      <section className="hrpage__panel hrstm" aria-label="월별 재직 인원">
        {head}
        <p className="hrstm__note">{year}년의 월별 재직 인원은 볼 수 없습니다 — 서버가 오늘부터 최근 12개월만 줍니다.</p>
      </section>
    );
  }
  if (trend === undefined) {
    return (
      <section className="hrpage__panel hrstm" aria-label="월별 재직 인원">
        {head}
        <p className="hrstm__note">불러오는 중…</p>
      </section>
    );
  }
  if (trend === null) {
    return (
      <section className="hrpage__panel hrstm" aria-label="월별 재직 인원">
        {head}
        <p className="hrstm__note">월별 재직 인원을 읽지 못했습니다 — 옛 서버이거나 볼 권한이 없습니다.</p>
      </section>
    );
  }
  const rows = monthlyDeltas(trend.months, year);
  const maxAbs = Math.max(1, ...rows.map((r) => Math.abs(r.delta ?? 0)));
  const h = (n: number) => (n === 0 ? 0 : Math.max(3, Math.round((Math.abs(n) / maxAbs) * 40)));
  return (
    <section className="hrpage__panel hrstm" aria-label="월별 재직 인원">
      {head}
      <div className="hrstm__cols" role="img"
        aria-label={`월별 재직 인원 전달 대비 증감: ${rows.map((r) => `${Number(r.month.slice(5))}월 ${r.delta === null ? '모름' : signed(r.delta)} (재직 ${r.active}명)`).join(', ')}`}>
        {rows.map((r) => (
          <div className="hrstm__col" key={r.month}>
            <span className="hrstm__dv">{r.delta === null ? '—' : signed(r.delta)}</span>
            {/* 위 · 아래 두 칸 — 0 기준선이 늘 가운데에 선다(막대는 0 에서 시작 · Few-08) */}
            <div className="hrstm__track" aria-hidden>
              <span className="hrstm__half hrstm__half--up">
                <i className="hrstm__up" style={{ height: `${r.delta && r.delta > 0 ? h(r.delta) : 0}px` }} />
              </span>
              <span className="hrstm__zero" />
              <span className="hrstm__half hrstm__half--down">
                <i className="hrstm__down" style={{ height: `${r.delta && r.delta < 0 ? h(r.delta) : 0}px` }} />
              </span>
            </div>
            <span className="hrstm__active num">{r.active}</span>
            <span className="hrstm__m">{Number(r.month.slice(5))}월</span>
          </div>
        ))}
      </div>
      <p className="hrstm__legend">
        막대 = 전달보다 늘거나 준 사람 · 아래 숫자 = 그달 말 재직(이번 달은 오늘) · 입사와 퇴사가 같은 달이면 0 으로 보입니다
        {trend.estimated && ` · 추정치 — 제외 ${trend.excluded.noHireDate + trend.excluded.undatedStatus}명(입사일 없음 ${trend.excluded.noHireDate} · 날짜 근거 없음 ${trend.excluded.undatedStatus})`}
      </p>
    </section>
  );
}

export function CareerLine({ records }: {
  records: { total: number; withCareer: number; avgCareerYears: number } | null | undefined;
}) {
  if (!records) return null;
  const pct = records.total > 0 ? Math.round((records.withCareer / records.total) * 100) : 0;
  return (
    <div className="hrstm__career">
      <p>
        입사 전 경력 등록 <b>{records.withCareer}명</b>({records.total}명 중 {pct}%)
        {' · '}입사 전 경력 평균 <b>{records.withCareer > 0 ? `${records.avgCareerYears}년` : '—'}</b>
        {records.withCareer > 0 && `(경력 있는 ${records.withCareer}명)`}
        {' · '}출처 경력 · 학력
      </p>
      <p className="hrstm__note">근속은 입사일부터 센 값이고, 입사 전 경력은 그 앞 회사에서 쌓은 경력입니다.</p>
    </div>
  );
}
